import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Bell, Loader2, RotateCcw, Save, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import type { NotificationCategoryKey, NotificationPreferences } from '@/types';

/**
 * NotificationPreferencesTab — v3-notif FASE 0 (2026-05-27).
 *
 * User-level notification preferences panel under `/profile?tab=notifications`.
 *
 * The data is lazy-loaded on first mount (the field is already exposed on
 * `GET /v1/users/me` so the AuthContext snapshot carries it in most cases —
 * we still call `getNotificationPreferences()` explicitly to be defensive
 * against stale snapshots and to avoid relying on the auth-refresh cycle for
 * post-save consistency).
 *
 * Default detection: when the user has never opened this panel, the BE
 * returns the prefs with `updatedAt._seconds === 0` (sentinel). We render a
 * subtle banner so the recruiter knows they're on the recommended defaults.
 */

const CATEGORY_KEYS: readonly NotificationCategoryKey[] = [
  'application_received',
  'application_screened',
  'application_state',
  'interview_lifecycle',
  'team_events',
] as const;

function isEpochZero(updatedAt: NotificationPreferences['updatedAt']): boolean {
  if (typeof updatedAt === 'string') return false;
  return updatedAt._seconds === 0 && updatedAt._nanoseconds === 0;
}

export const NotificationPreferencesTab = () => {
  const { t } = useLanguage();
  const { user, refreshSession } = useAuth();

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(
    user?.notificationPreferences ?? null,
  );
  const [loading, setLoading] = useState(!user?.notificationPreferences);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Re-fetch on mount when AuthContext didn't carry the field yet (e.g. user
  // logged in before BE deploy). Best-effort — the repository in the BE never
  // throws on read, so failures here usually mean a transient network blip.
  useEffect(() => {
    if (prefs) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const p = await api.auth.getNotificationPreferences();
        if (!cancelled) setPrefs(p);
      } catch {
        if (!cancelled) toast.error(t('notifPrefs.saveError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !prefs) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10 text-muted-foreground gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">{t('common.loading') || 'Loading…'}</span>
        </CardContent>
      </Card>
    );
  }

  const usingDefaults = isEpochZero(prefs.updatedAt);

  const toggleEmail = (cat: NotificationCategoryKey, v: boolean) => {
    setPrefs((p) => (p ? { ...p, emailEnabled: { ...p.emailEnabled, [cat]: v } } : p));
  };

  const handleSave = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const saved = await api.auth.putNotificationPreferences({
        emailEnabled: prefs.emailEnabled,
        inAppEnabled: prefs.inAppEnabled,
        digestEnabled: prefs.digestEnabled,
        digestHourLocal: prefs.digestHourLocal,
      });
      setPrefs(saved);
      toast.success(t('notifPrefs.saved'));
      // Refresh session so AuthContext picks up the new updatedAt — keeps the
      // "using defaults" banner consistent without a hard reload.
      try { await refreshSession(); } catch { /* non-fatal */ }
    } catch {
      toast.error(t('notifPrefs.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await api.auth.resetNotificationPreferences();
      const fresh = await api.auth.getNotificationPreferences();
      setPrefs(fresh);
      setResetOpen(false);
      toast.success(t('notifPrefs.resetDone'));
      try { await refreshSession(); } catch { /* non-fatal */ }
    } catch {
      toast.error(t('notifPrefs.saveError'));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-4 w-4" />
            {t('notifPrefs.heading')}
          </CardTitle>
          <p className="text-xs text-muted-foreground pt-1">{t('notifPrefs.subheading')}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {usingDefaults && (
            <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-primary">
              {t('notifPrefs.usingDefaultsBanner')}
            </div>
          )}
          <div className="divide-y divide-border/50">
            {CATEGORY_KEYS.map((cat) => (
              <div key={cat} className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{t(`notifPrefs.cat.${cat}.title`)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t(`notifPrefs.cat.${cat}.desc`)}
                  </p>
                </div>
                <Switch
                  checked={prefs.emailEnabled[cat]}
                  onCheckedChange={(v) => toggleEmail(cat, v)}
                  aria-label={t(`notifPrefs.cat.${cat}.title`)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('notifPrefs.master.inApp.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <p className="text-xs text-muted-foreground flex-1">
              {t('notifPrefs.master.inApp.desc')}
            </p>
            <Switch
              checked={prefs.inAppEnabled}
              onCheckedChange={(v) => setPrefs((p) => (p ? { ...p, inAppEnabled: v } : p))}
              aria-label={t('notifPrefs.master.inApp.title')}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('notifPrefs.digest.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <p className="text-xs text-muted-foreground flex-1">
              {t('notifPrefs.digest.desc')}
            </p>
            <Switch
              checked={prefs.digestEnabled}
              onCheckedChange={(v) => setPrefs((p) => (p ? { ...p, digestEnabled: v } : p))}
              aria-label={t('notifPrefs.digest.title')}
            />
          </div>
          {prefs.digestEnabled && (
            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-border/40">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('notifPrefs.digest.hourLabel')}</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  step={1}
                  value={prefs.digestHourLocal}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    const clamped = Number.isFinite(raw)
                      ? Math.max(0, Math.min(23, Math.round(raw)))
                      : 9;
                    setPrefs((p) => (p ? { ...p, digestHourLocal: clamped } : p));
                  }}
                />
                <p className="text-[11px] text-muted-foreground">
                  {t('notifPrefs.digest.hourHint')}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={handleSave} disabled={saving} size="sm">
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          {t('notifPrefs.save')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setResetOpen(true)}
          disabled={saving || usingDefaults}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('notifPrefs.reset')}
        </Button>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 ml-auto">
          <ShieldCheck className="h-3 w-3" />
          {t('notifPrefs.complianceNote')}
        </p>
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('notifPrefs.resetConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('notifPrefs.resetConfirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} disabled={resetting}>
              {resetting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t('notifPrefs.reset')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NotificationPreferencesTab;
